// External imports
import React, { useRef, useState } from "react";
import { AppBar, Toolbar, Box, Container, useTheme } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import GridViewIcon from "@mui/icons-material/GridView";
import HeaderMenuButton from "../../components/HeaderMenuButton.js";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SortIcon from "@mui/icons-material/Sort";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";

// Internal imports
import HideOnScroll from "../../components/HideOnScroll.js";
import ListMenu from "../../components/ListMenu.js";
import SortMenu from "../../components/SortMenu.js";
import CheckboxFilterMenu from "../../components/CheckboxFilterMenu.js";
import RatingFilterMenu from "../../components/RatingFilterMenu.js";
import FinishDateFilterMenu from "../../components/FinishDateFilterMenu.js";
import ReleaseYearFilterMenu from "../../components/ReleaseYearFilterMenu.js";
import SearchFilter from "../../components/SearchFilter.js";
import ActiveFilterChips from "./ActiveFilterChips.js";
import {
  HeaderButton,
  HeaderToggleButtonGroup,
  HeaderToggleButton,
} from "../../components/CustomMUI.js";
import {
  getContentOptions,
  getStatusOptions,
  getContentOptionsArray,
  getStatusOptionsArray,
  getSortByOptions,
  getFilterOptions,
  getGenreOptions,
  FILTER_CONFIG,
  getFilteredReactionOptions,
  getLanguageLabel,
} from "../../utils/utils.js";
import { useAuth } from "../../context/authContext.js";
import { useNavigate } from "react-router-dom";

const TMDB_IMAGE_BASE = "https://image.tmdb.org/t/p/w92";
// Value of the "Clear" item in single-choice filter menus (options need a
// non-null value, which ListMenu also uses as the item's key)
const CLEAR_CHOICE = "__clear__";
// Person suggestions shown under the search field at once — kept short (as
// are the titles, see Track) so both fit above the mobile keyboard
const MAX_PERSON_SUGGESTIONS = 5;

// Strips accents and case so "almodovar" finds "Almodóvar"
const normaliseName = (name) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Vertical rhythm (px): one SECTION_GAP between the button row, the filter
// chip row, Track's summary banner and the list, so each sits evenly spaced
const HEADER_TOP_PADDING = 16;
const BUTTON_ROW_HEIGHT = 38; // HeaderButton's height
// The filter chip row's height (small chips), which Track's summary banner
// shares so the two rows read as a matching pair
export const CHIP_ROW_HEIGHT = 24;
export const SECTION_GAP = 12;

// Curate and Explore's spacer leaves this much above and below a full Toolbar
const OTHER_PAGES_SPACER_MARGIN = 20;

/**
 * Responsive sx setting `property` to `getPx(toolbarHeight)` at each
 * breakpoint, mirroring theme.mixins.toolbar — for spacing that matches
 * Curate and Explore, whose headers are sized by a regular Toolbar.
 */
const byToolbarHeightSx = (theme, property, getPx) => {
  const mapHeights = (mixin) =>
    Object.fromEntries(
      Object.entries(mixin).map(([key, value]) =>
        key === "minHeight"
          ? [property, `${getPx(value)}px`]
          : [key, mapHeights(value)],
      ),
    );
  return mapHeights(theme.mixins.toolbar);
};

// Curate and Explore centre their buttons in a regular Toolbar. Track's row
// is only as tall as its buttons (keeping the gaps below it even), so it adds
// half a Toolbar's spare height above them to sit at the same distance from
// the top of the screen
const getButtonRowTop = (toolbarHeight) =>
  HEADER_TOP_PADDING + (toolbarHeight - BUTTON_ROW_HEIGHT) / 2;

/**
 * Space Track adds below its header when the summary strip doesn't show (an
 * empty tab of your own), so the empty state starts as far below the header
 * buttons as Curate's content does, rather than a SECTION_GAP under them.
 */
export const emptyTabOffsetSx = (theme) =>
  byToolbarHeightSx(
    theme,
    "height",
    (toolbarHeight) =>
      2 * OTHER_PAGES_SPACER_MARGIN +
      toolbarHeight -
      (getButtonRowTop(toolbarHeight) + BUTTON_ROW_HEIGHT + SECTION_GAP),
  );

// Each header row scrolls sideways on its own when it overflows (scrollbar hidden)
const scrollRowSx = {
  overflowX: "auto",
  "&::-webkit-scrollbar": { display: "none" },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
};

const LibraryHeader = ({
  view,
  onViewChange,
  sortBy,
  onSortByChange,
  sortDescending,
  toggleSortDescending,
  groupByMonth,
  onGroupByMonthToggle,
  toggleDrawer,
  activeFilters,
  onActiveFiltersChange,
  searchTerm,
  onSearchTermChange,
  status,
  onStatusChange,
  content,
  onContentChange,
  drawerWidth,
  onDynamicFilterOpen,
  filterOptions,
  filterOptionsLoading,
  onTitleQueryChange,
  titleSuggestions,
  onClearAll,
}) => {
  // Hooks
  const { user, openAuthDialog } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  // Filter options
  // Track has an All view, so its content picker offers it
  const contentOptions = getContentOptions({ showEverythingOption: true });
  const statusOptions = getStatusOptions(theme);
  const sortByOptions = getSortByOptions(status);
  const filterMenuOptions = getFilterOptions(status, content);
  const genreOptions = getGenreOptions({ type: content });

  const statusOptionsArray = getStatusOptionsArray(theme).filter(
    (option) => option.value !== "none",
  );

  const contentOptionsArray = getContentOptionsArray({
    showEverythingOption: true,
  }).map((option) => ({
    ...option,
    label: option.labelPlural,
  }));

  const reactionOptions = React.useMemo(() => {
    return getFilteredReactionOptions(content).map((opt) => ({
      value: opt.label,
      label: opt.label,
      icon: opt.icon ?? null,
      emoji: opt.emoji ?? null,
    }));
  }, [content]);

  const languageFilterOptions = (filterOptions.Language ?? []).map((lang) => ({
    value: lang.code,
    label: getLanguageLabel(lang.code),
  }));

  const currentSortBy = sortByOptions.find((option) => option.value === sortBy);

  // Already narrowed server-side to the viewer's own streaming services
  const providerFilterOptions = (filterOptions.Provider ?? []).map((p) => ({
    value: p.id,
    label: p.name,
    icon: p.logo_path ? (
      <img
        src={`${TMDB_IMAGE_BASE}${p.logo_path}`}
        alt={p.name}
        style={{ width: 20, height: 20, borderRadius: 2 }}
      />
    ) : null,
  }));

  // Every checkbox-style filter's options, keyed by filter type
  const checkboxFilterOptions = {
    Genre: genreOptions,
    // People are added through search; this menu (from the chip) only
    // unticks the ones already chosen
    Person: (filterOptions.Person ?? [])
      .filter((person) =>
        activeFilters
          .find((f) => f.type === "Person")
          ?.value.includes(person.id),
      )
      .map((person) => ({ value: person.id, label: person.name })),
    Provider: providerFilterOptions,
    Reaction: reactionOptions,
    Language: languageFilterOptions,
  };

  // Single-choice filters, as [{ label, value }] where value is the URL token
  const choiceFilterOptions = {
    Note: [
      { label: "With note", value: "true" },
      { label: "Without note", value: "false" },
    ],
  };

  const providerFooterAction = {
    label:
      providerFilterOptions.length === 0
        ? "Choose your streaming services"
        : "Edit your streaming services",
    onClick: !user
      ? () => openAuthDialog("Filtering by streaming service")
      : () => navigate("/settings/services"),
  };

  // Search: picks a title or person from suggestions rather than filtering as
  // you type. Titles come from Track (a server lookup); people are matched
  // here against the filter options (most frequent in the library first),
  // leaving out anyone already filtered by.
  const [searchQuery, setSearchQuery] = useState("");
  const personFilterLabel = filterMenuOptions.find(
    (o) => o.value === "Person",
  ).label;
  const selectedPeople =
    activeFilters.find((f) => f.type === "Person")?.value ?? [];

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
    onTitleQueryChange(query);
  };

  const normalisedQuery = normaliseName(searchQuery);
  const personSuggestions = (filterOptions.Person ?? [])
    .filter(
      (person) =>
        !selectedPeople.includes(person.id) &&
        normaliseName(person.name).includes(normalisedQuery),
    )
    .slice(0, MAX_PERSON_SUGGESTIONS)
    .map((person) => ({
      kind: "person",
      key: `person-${person.id}`,
      personId: person.id,
      label: person.name,
      // All mixes directors, creators and authors, so say which
      secondary: content === "all" ? person.role : undefined,
      icon: <PersonIcon fontSize="small" />,
    }));

  const titleItems = searchQuery
    ? [
        // Always first, so a partial search (and Enter) is never a dead end
        {
          kind: "contains",
          key: "contains",
          label: `Titles containing "${searchQuery}"`,
          term: searchQuery,
          icon: <SearchIcon fontSize="small" />,
        },
        ...titleSuggestions.map((item) => ({
          kind: "title",
          key: `title-${item.content_id}`,
          label: item.content_title,
          term: item.content_title,
          icon: item.image_url ? (
            <Box
              component="img"
              alt=""
              src={
                item.content_type === "book"
                  ? `https://images.isbndb.com/covers/${item.image_url}.jpg`
                  : `${TMDB_IMAGE_BASE}/${item.image_url}`
              }
              sx={{
                width: 24,
                height: 36,
                objectFit: "cover",
                borderRadius: 0.5,
              }}
            />
          ) : null,
        })),
      ]
    : [];

  const suggestionSections = [
    { title: "Titles", items: titleItems },
    {
      title: content === "all" ? "People" : `${personFilterLabel}s`,
      items: personSuggestions,
    },
  ];

  // A title applies as the search term (shown as a chip); a person joins the
  // person filter
  const handleSuggestionSelect = (item) => {
    if (item.kind === "person") {
      onActiveFiltersChange([
        ...activeFilters.filter((f) => f.type !== "Person"),
        { type: "Person", value: [...selectedPeople, item.personId] },
      ]);
    } else {
      onSearchTermChange(item.term);
    }
  };

  // Anchor elements
  const [filterPrimaryAnchorEl, setFilterPrimaryAnchorEl] = useState(null);
  const [filterSecondaryAnchorEl, setFilterSecondaryAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [contentAnchorEl, setContentAnchorEl] = useState(null);

  // Refs
  const filterButtonRef = useRef(null);
  const searchFilterRef = useRef(null);

  // Filter state
  // The filter whose own menu (editor) is open, if any
  const [activeFilterMenu, setActiveFilterMenu] = useState("");
  // Selection being edited in the open checkbox menu; only written to the
  // URL (via onActiveFiltersChange) on Apply
  const [draftValue, setDraftValue] = useState([]);

  //////////////////////////////////////
  // Core filter logic

  const handlePrimaryFilterClick = (event) => {
    setFilterPrimaryAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleContentClick = (event) => {
    setContentAnchorEl(event.currentTarget);
  };

  const handleCloseMenus = () => {
    setFilterPrimaryAnchorEl(null);
    setFilterSecondaryAnchorEl(null);
    setSortAnchorEl(null);
    setStatusAnchorEl(null);
    setContentAnchorEl(null);
    setActiveFilterMenu("");
    setDraftValue([]);
  };

  // Picking a filter in the Filter menu opens its editor under the Filter
  // button — except people, which are picked through the search field, and
  // toggles, which apply straight away
  const handleFilterSelect = (type) => () => {
    setFilterPrimaryAnchorEl(null);
    if (type === "Person") {
      // Synchronous, so the mobile keyboard opens (see focusWithText)
      searchFilterRef.current?.focusWithText();
      return;
    }
    if (FILTER_CONFIG.find((c) => c.type === type)?.toggle) {
      applySingleValueFilter(type, "true");
      return;
    }
    openFilterEditor(type, filterButtonRef.current);
  };

  const handleSortSelect = (option) => {
    onSortByChange(null, option);
    handleCloseMenus();
  };

  const handleStatusSelect = (option) => (event) => {
    // This function needs to accept an event even though it is not used as that is how the onClick prop of the listMenu component is configured
    onStatusChange(option);
    handleCloseMenus();
  };

  const handleContentSelect = (option) => (event) => {
    // This function needs to accept an event even though it is not used as that is how the onClick prop of the listMenu component is configured
    onContentChange(option);
    handleCloseMenus();
  };

  // Opens a filter's own menu (editor), from its chip or the Filter menu
  const openFilterEditor = (type, anchorEl) => {
    setActiveFilterMenu(type);
    setFilterSecondaryAnchorEl(anchorEl);

    const currentFilter = activeFilters.find((filter) => filter.type === type);
    setDraftValue(currentFilter ? currentFilter.value : []);

    if (FILTER_CONFIG.find((c) => c.type === type)?.dynamic) {
      onDynamicFilterOpen();
    }
  };

  const removeFilter = (type) => {
    onActiveFiltersChange(activeFilters.filter((f) => f.type !== type));
  };

  // Applying an empty selection removes the filter
  const handleApplyFilter = () => {
    if (draftValue.length > 0) {
      onActiveFiltersChange([
        ...activeFilters.filter((f) => f.type !== activeFilterMenu),
        { type: activeFilterMenu, value: draftValue },
      ]);
    } else {
      removeFilter(activeFilterMenu);
    }
    handleCloseMenus();
  };

  const handleClearFilter = () => {
    removeFilter(activeFilterMenu);
    handleCloseMenus();
  };

  // Sets a single-value filter (rating, finish date, ...) and closes its menu
  const applySingleValueFilter = (type, value) => {
    onActiveFiltersChange([
      ...activeFilters.filter((f) => f.type !== type),
      { type, value },
    ]);
    handleCloseMenus();
  };

  // Curried because ListMenu calls onSelect(value) to get each item's click handler
  const handleChoiceSelect = (value) => () => {
    if (value === CLEAR_CHOICE) {
      handleClearFilter();
    } else {
      applySingleValueFilter(activeFilterMenu, value);
    }
  };

  // Menus offer only what applies to this tab — options the tab can't use
  // (e.g. rating off the finished tab) are left out rather than greyed out
  const availableSortByOptions = sortByOptions.filter((o) => !o.disabled);
  const availableFilterOptions = filterMenuOptions
    .filter((o) => !o.disabled)
    // No divider trailing the menu once what followed it is left out
    .map((o, index, shown) =>
      index === shown.length - 1 ? { ...o, dividerAfter: false } : o,
    );

  const hasActiveFilters = activeFilters.length > 0;
  // The chip row also carries an applied title search
  const showChipRow = hasActiveFilters || !!searchTerm;

  return (
    <>
      {/* Holds the fixed app bar's place in the page: its top padding, button
      row and, while it shows, the chip row (gap + CHIP_ROW_HEIGHT), then
      SECTION_GAP before the page content. Ends below the bar itself, so the
      bar covers no more of the page than it needs to. */}
      <Box
        sx={byToolbarHeightSx(
          theme,
          "height",
          (toolbarHeight) =>
            getButtonRowTop(toolbarHeight) +
            BUTTON_ROW_HEIGHT +
            (showChipRow ? SECTION_GAP + CHIP_ROW_HEIGHT : 0) +
            SECTION_GAP,
        )}
      />
      <HideOnScroll>
        <AppBar
          color="inherit"
          sx={{
            boxShadow: "none",
            backgroundImage: "none", // needed to remove default overlay MUI adds in dark mode to desaturate colour
            width: { md: `calc(100% - ${drawerWidth}px )` },
            ml: { md: `${drawerWidth}px` },
            ...byToolbarHeightSx(theme, "paddingTop", getButtonRowTop),
            pb: `${SECTION_GAP}px`,
          }}
        >
          <Box sx={scrollRowSx}>
            <Container maxWidth="lg">
              {/* Exactly the buttons' height, so the gaps around it are only
                  SECTION_GAP rather than whatever a Toolbar's min height leaves */}
              <Toolbar
                disableGutters
                variant="dense"
                sx={{ minHeight: BUTTON_ROW_HEIGHT }}
              >
                <HeaderMenuButton onClick={toggleDrawer} sx={{ mr: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    flexGrow: 1,
                    flexShrink: 0,
                    justifyContent: "flex-start",
                  }}
                >
                  <HeaderButton
                    startIcon={contentOptions[content].icon}
                    endIcon={<ExpandMoreIcon />}
                    onClick={handleContentClick}
                    sx={{ mr: 1 }}
                    // mobile={isMobile}
                  >
                    {contentOptions[content].labelPlural}
                  </HeaderButton>

                  <HeaderButton
                    startIcon={statusOptions[status].iconWhite}
                    endIcon={<ExpandMoreIcon />}
                    onClick={handleStatusClick}
                    sx={{
                      mr: 1,
                      color: statusOptions[status].contrastText,
                      backgroundColor: statusOptions[status].color,
                      "&:hover": {
                        backgroundColor: statusOptions[status].color,
                      },
                    }}
                    // mobile={isMobile}
                  >
                    {statusOptions[status].label}
                  </HeaderButton>

                  <HeaderButton
                    ref={filterButtonRef}
                    startIcon={<FilterAltIcon />}
                    onClick={handlePrimaryFilterClick}
                    endIcon={<ExpandMoreIcon />}
                    sx={{ mr: 1 }}
                    // mobile={isMobile}
                  >
                    {hasActiveFilters
                      ? `${activeFilters.length > 1 ? "Filters" : "Filter"} · ${activeFilters.length}`
                      : "Filter"}
                  </HeaderButton>

                  <HeaderButton
                    startIcon={
                      <SortIcon
                        style={{
                          transform: sortDescending ? "none" : "scaleY(-1)", // Flip vertically for ascending sort
                          transition: "transform 0.2s ease", // Optional: Add a smooth transition
                        }}
                      />
                    }
                    onClick={handleSortClick}
                    endIcon={<ExpandMoreIcon />}
                    sx={{ mr: 1 }}
                    // mobile={isMobile}
                  >
                    {currentSortBy.label}
                  </HeaderButton>
                </Box>

                <SearchFilter
                  ref={searchFilterRef}
                  placeholder={`Title or ${personFilterLabel.toLowerCase()}`}
                  suggestionSections={suggestionSections}
                  onQueryChange={handleSearchQueryChange}
                  onSuggestionSelect={handleSuggestionSelect}
                  // People come from the filter options, fetched on first use
                  onFocus={onDynamicFilterOpen}
                />

                <HeaderToggleButtonGroup
                  value={view}
                  exclusive
                  size="small"
                  onChange={onViewChange}
                  aria-label="change view"
                >
                  <HeaderToggleButton value="grid" aria-label="grid view">
                    <GridViewIcon />
                  </HeaderToggleButton>
                  <HeaderToggleButton
                    value="list"
                    aria-label="list view"
                    sx={{ mr: { xs: 2, md: 0 } }}
                  >
                    <FormatListBulletedIcon />
                  </HeaderToggleButton>
                </HeaderToggleButtonGroup>

                {/* Popup menus... */}

                {sortAnchorEl && (
                  <SortMenu
                    anchorEl={sortAnchorEl}
                    onClose={handleCloseMenus}
                    onSelect={handleSortSelect}
                    sortByValue={sortBy}
                    toggleValue={sortDescending}
                    onToggle={toggleSortDescending}
                    sortByOptions={availableSortByOptions}
                    groupByMonth={groupByMonth}
                    onGroupByMonthToggle={onGroupByMonthToggle}
                  />
                )}

                {filterPrimaryAnchorEl && (
                  <ListMenu
                    anchorEl={filterPrimaryAnchorEl}
                    onClose={handleCloseMenus}
                    onSelect={handleFilterSelect}
                    // Active filters are highlighted
                    options={availableFilterOptions.map((option) => ({
                      ...option,
                      selected: activeFilters.some(
                        (f) => f.type === option.value,
                      ),
                    }))}
                    // Its Director item hands focus to the search field
                    releaseFocus
                  />
                )}

                {statusAnchorEl && (
                  <ListMenu
                    anchorEl={statusAnchorEl}
                    onClose={handleCloseMenus}
                    onSelect={handleStatusSelect}
                    options={statusOptionsArray}
                    selectedValue={status}
                  />
                )}

                {contentAnchorEl && (
                  <ListMenu
                    anchorEl={contentAnchorEl}
                    onClose={handleCloseMenus}
                    onSelect={handleContentSelect}
                    options={contentOptionsArray}
                    selectedValue={content}
                  />
                )}

                {checkboxFilterOptions[activeFilterMenu] && (
                  <CheckboxFilterMenu
                    options={checkboxFilterOptions[activeFilterMenu]}
                    selectedOptions={draftValue}
                    onChange={setDraftValue}
                    onApply={handleApplyFilter}
                    onClear={handleClearFilter}
                    anchorEl={filterSecondaryAnchorEl}
                    onClose={handleCloseMenus}
                    loading={
                      FILTER_CONFIG.find((c) => c.type === activeFilterMenu)
                        ?.dynamic && filterOptionsLoading
                    }
                    footerAction={
                      activeFilterMenu === "Provider"
                        ? providerFooterAction
                        : undefined
                    }
                    selectAll={activeFilterMenu === "Provider"}
                  />
                )}

                {activeFilterMenu === "Rating" && (
                  <RatingFilterMenu
                    value={
                      activeFilters.find((filter) => filter.type === "Rating")
                        ?.value
                    }
                    onApply={(token) => applySingleValueFilter("Rating", token)}
                    onClear={handleClearFilter}
                    anchorEl={filterSecondaryAnchorEl}
                    onClose={handleCloseMenus}
                  />
                )}

                {activeFilterMenu === "Finished" && (
                  <FinishDateFilterMenu
                    value={
                      activeFilters.find((filter) => filter.type === "Finished")
                        ?.value
                    }
                    onApply={(token) =>
                      applySingleValueFilter("Finished", token)
                    }
                    onClear={handleClearFilter}
                    anchorEl={filterSecondaryAnchorEl}
                    onClose={handleCloseMenus}
                  />
                )}

                {activeFilterMenu === "Year" && (
                  <ReleaseYearFilterMenu
                    value={
                      activeFilters.find((filter) => filter.type === "Year")
                        ?.value
                    }
                    yearOptions={filterOptions.Year ?? []}
                    loading={filterOptionsLoading}
                    onApply={(token) => applySingleValueFilter("Year", token)}
                    onClear={handleClearFilter}
                    anchorEl={filterSecondaryAnchorEl}
                    onClose={handleCloseMenus}
                  />
                )}

                {choiceFilterOptions[activeFilterMenu] && (
                  <ListMenu
                    anchorEl={filterSecondaryAnchorEl}
                    onClose={handleCloseMenus}
                    onSelect={handleChoiceSelect}
                    options={[
                      ...choiceFilterOptions[activeFilterMenu],
                      { label: "Clear", value: CLEAR_CHOICE },
                    ]}
                    selectedValue={
                      activeFilters.find((f) => f.type === activeFilterMenu)
                        ?.value ?? null
                    }
                  />
                )}
              </Toolbar>
            </Container>
          </Box>

          {showChipRow && (
            <Box sx={{ ...scrollRowSx, mt: `${SECTION_GAP}px` }}>
              <Container maxWidth="lg">
                <ActiveFilterChips
                  filters={activeFilters}
                  status={status}
                  content={content}
                  filterOptions={filterOptions}
                  onChipClick={(type, event) =>
                    openFilterEditor(type, event.currentTarget)
                  }
                  onRemove={removeFilter}
                  searchTerm={searchTerm}
                  // Reopens the search with the term, to edit or replace it
                  onSearchChipClick={() =>
                    searchFilterRef.current?.focusWithText(searchTerm)
                  }
                  onSearchRemove={() => onSearchTermChange("")}
                  onClearAll={onClearAll}
                />
              </Container>
            </Box>
          )}
        </AppBar>
      </HideOnScroll>
    </>
  );
};

export default LibraryHeader;
